'use client';

import { useState, useEffect } from 'react';
import { useTranslationDetector } from '@/hooks/useTranslationDetector';
import { useModal } from '@/contexts/ModalContext';
import { byPrefixAndName } from '@awesome.me/kit-12cf79935c/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Modal from '@/components/ui/Modal';
import LandingFilterModalContent from './LandingFilterModalContent';
import clsx from 'clsx';

export default function LandingFiltersPanelMobile({ 
    onSortChange, 
    initialSort = '0',
    currentSortId = '0',
    filters = {},
    onFilterChange = () => {},
    filterOptions = {}
}) {
    const t = useTranslationDetector('hotel');
    const sortModal = useModal('landing-filter-mobile-sort');
    const filterModal = useModal('landing-filter-mobile-filter');
    const [initialScrollTo, setInitialScrollTo] = useState(null);

    const [activeSort, setActiveSort] = useState(initialSort);

    // initialSort değiştiğinde activeSort'u güncelle
    useEffect(() => {
        setActiveSort(initialSort);
    }, [initialSort]);

    const sortOptions = [
        { id: '0', label: t('sortRecommended') },
        { id: '6', label: t('sortMostStars') },
        { id: '4', label: t('sortHighestRating') },
    ];

    const handleSortClick = (sortId) => {
        setActiveSort(sortId);
        sortModal.close();
        
        if (onSortChange) {
            onSortChange(sortId);
        }
    };

    // Seçili sıralama seçeneğini bul
    const selectedSortOption = sortOptions.find(opt => opt.id === activeSort) || sortOptions[0];

    // Filtre durumlarını kontrol et
    const isRatingActive = filters.rating === '9+';
    const hasLocationFilters = filters.locations && filters.locations.length > 0;
    const hasThemeFilters = filters.themes && filters.themes.length > 0;

    // 9+ Puan hızlı filtre handler
    const handleRatingToggle = () => {
        const newRating = isRatingActive ? '' : '9+';
        const updatedFilters = { ...filters, rating: newRating };
        onFilterChange(updatedFilters);
    };

    const filterButtons = [
        {
            id: 'sort',
            label: `${t('sortLabel')}: ${selectedSortOption.label}`,
            icon: byPrefixAndName.far['bars-sort'] || byPrefixAndName.fas['bars-sort'],
            onClick: () => sortModal.open({ isBottomSheet: true, size: 'full' }),
            isActive: false,
        },
        {
            id: 'filter',
            label: t('filterLabel'),
            icon: byPrefixAndName.far['filter'],
            onClick: () => {
                setInitialScrollTo(null);
                filterModal.open({ isBottomSheet: true, size: 'full' });
            },
            isActive: false,
        },
        {
            id: 'rating',
            label: t('rating9Plus'),
            onClick: handleRatingToggle,
            isActive: isRatingActive,
        },
        {
            id: 'region',
            label: t('region'),
            hasDropdown: true,
            onClick: () => {
                setInitialScrollTo('location');
                filterModal.open({ isBottomSheet: true, size: 'full' });
            },
            isActive: hasLocationFilters,
        },
        {
            id: 'theme',
            label: t('theme'),
            hasDropdown: true,
            onClick: () => {
                setInitialScrollTo('themes');
                filterModal.open({ isBottomSheet: true, size: 'full' });
            },
            isActive: hasThemeFilters,
        },
    ];

    return (
        <>
            <div className="flex gap-2 overflow-x-scroll scrollbar-hide -mx-4 sm:-mx-6 px-4 sm:px-6">
                {filterButtons.map((button) => (
                    <button
                        key={button.id}
                        onClick={button.onClick}
                        className={clsx(
                            'flex items-center gap-[6px] px-[10px] py-2 rounded-lg',
                            'border bg-white',
                            'text-sm font-normal',
                            'whitespace-nowrap shrink-0',
                            'transition-colors cursor-pointer',
                            'shadow-sm',
                            button.isActive
                                ? 'border-primary bg-primary-weaker text-primary'
                                : 'border-[#D0D0DC] text-foreground hover:bg-gray-50 active:bg-gray-100'
                        )}
                    >
                        {button.icon && (
                            <FontAwesomeIcon
                                icon={button.icon}
                                className="text-base"
                            />
                        )}
                        <span>{button.label}</span>
                        {button.hasDropdown && (
                            <FontAwesomeIcon
                                icon={byPrefixAndName.fas['chevron-down']}
                                className="text-xs text-gray-400"
                            />
                        )}
                    </button>
                ))}
            </div>

            {/* Modals */}
            <Modal id="landing-filter-mobile-sort" title={t('sortLabel')} titleCentered>
                <div className="-mt-6">
                    {sortOptions.map((option) => {
                        const isActive = activeSort === option.id;
                        return (
                            <button
                                key={option.id}
                                onClick={() => handleSortClick(option.id)}
                                className={clsx(
                                    'w-full flex items-center justify-between py-5 border-b border-[#E8E8E9]',
                                    'text-left transition-colors',
                                    isActive
                                        ? 'text-primary'
                                        : 'text-foreground'
                                )}
                            >
                                <span className={clsx(
                                    'text-base font-normal',
                                    isActive && 'text-primary !font-medium'
                                )}>
                                    {option.label}
                                </span>
                                {isActive && (
                                    <FontAwesomeIcon
                                        icon={byPrefixAndName.fas['check'] || byPrefixAndName.far['check']}
                                        className="text-foreground text-lg"
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>
            </Modal>

            <Modal 
                id="landing-filter-mobile-filter" 
                title={t('filterTitle')} 
                titleCentered
                isBottomSheet={true}
                isFullScreen={false}
                useSafeViewport={true}
                contentClassName="p-0 flex flex-col h-full overflow-hidden"
            >
                <LandingFilterModalContent
                    filters={filters}
                    onFilterChange={onFilterChange}
                    filterOptions={filterOptions}
                    currentSortId={currentSortId}
                    onApply={() => {}}
                    onReset={() => {}}
                    onClose={() => {
                        filterModal.close();
                        setInitialScrollTo(null);
                    }}
                    initialScrollTo={initialScrollTo}
                />
            </Modal>
        </>
    );
}

